<template>
  <div class="psui-el-dropdown-menu">
    <div
      ref="PSDropdownTrigger"
      @click="toggle"
      @keydown.enter.stop.prevent="toggle"
      @keydown.space.stop.prevent="toggle"
    >
      <slot
        v-if="hasDropdownTrigger"
        name="dropdownTrigger"
        :show="show"
      />
      <button
        v-else
        type="button"
        :aria-expanded="show"
      >
        <slot
          v-if="show && $slots.buttonLabelOnShow"
          name="buttonLabelOnShow"
        />
        <slot
          v-else
          name="buttonLabel"
        />
      </button>
    </div>
    <Teleport to="#teleport-target">
      <Transition name="fade">
        <div
          v-if="show"
          ref="PSDropdownFloating"
          :role="role"
          tabindex="-1"
          class="psui-el-dropdown-menu-dialog-wrapper"
          @keydown.esc.stop.prevent="closeWithFocus"
          @keydown.down.prevent="moveFocus(1)"
          @keydown.up.prevent="moveFocus(-1)"
          @keydown.tab="handleTab"
          :class="contentCss"
          aria-orientation="vertical"
          :style="floatingStyles"
          v-click-outside.capture="close"
        >
          <slot name="items" />
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
// Figma - 2.3 Dropdown with category divider https://www.figma.com/file/Tto8hrNlSfuPcwd1pfqogF/%E2%9A%A1%EF%B8%8F-Design-System?node-id=1768%3A64886
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  size,
} from '@floating-ui/vue'

import { ref, defineProps, useSlots, computed } from 'vue'
import Teleport from 'vue2-teleport'
import { nextTick } from 'vue'

const props = defineProps({
  disabled: { type: Boolean, default: false },
  placement: { type: String, default: 'bottom-start' },
  offsetVal: { type: Number, default: 4 },
  width: {
    type: [Number, String],
    default: null,
  },
  height: {
    type: [Number, String],
    default: 'auto',
  },
  contentCss: {
    type: String,
    default: 'psui-rounded psui-bg-white psui-shadow-elevation-20',
  },
  role: {
    type: String,
    default: 'menu',
    validator: (value) => ['menu', 'listbox'].includes(value),
  },
})

const emit = defineEmits(['open', 'close'])

const slots = useSlots()

const hasDropdownTrigger = computed(() => {
  return !!slots.dropdownTrigger
})

const show = ref(false)

const PSDropdownTrigger = ref(null)
const PSDropdownFloating = ref(null)

const { floatingStyles } = useFloating(PSDropdownTrigger, PSDropdownFloating, {
  placement: props.placement,
  whileElementsMounted: autoUpdate,
  middleware: [
    offset(props.offsetVal),
    flip(),
    shift(),
    size({
      apply({ rects, elements }) {
        const customWidth = props.width ?? rects.reference.width
        Object.assign(elements.floating.style, {
          width: `${customWidth}px`,
          ...(typeof height == 'string'
            ? { maxHeight: props.height }
            : { maxHeight: `${props.height}px` }),
        })
      },
    }),
  ],
})

const closeWithFocus = async () => {
  show.value = false
  emit('close')
  await nextTick()
  PSDropdownTrigger.value?.querySelector('button, [tabindex]:not([tabindex="-1"])')?.focus()
}

const focusFirstElement = async () => {
  await nextTick()
  if (PSDropdownFloating.value) {
    const focusable = PSDropdownFloating.value.querySelector(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"]), [role="combobox"], [tabindex="0"]',
    )
    focusable?.focus()
  }
}

const moveFocus = (offset) => {
  if (!PSDropdownFloating.value) return

  const focusableElements = PSDropdownFloating.value.querySelectorAll(
    'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  )
  
  const focusable = Array.from(focusableElements)
  if (focusable.length === 0) return

  const currentIndex = focusable.indexOf(document.activeElement)

  let nextIndex = currentIndex + offset
  
  if (nextIndex < 0) {
    nextIndex = focusable.length - 1 
  } else if (nextIndex >= focusable.length) {
    nextIndex = 0 
  }

  focusable[nextIndex].focus()
}

const handleTab = (e) => {
  if (e.key !== 'Tab' || !PSDropdownFloating.value) return

  const focusableElements = Array.from(PSDropdownFloating.value.querySelectorAll(
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), .psui-el-button:not(.is-disabled)'
  )).filter(el => {
    const isVisible = el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0
    return isVisible && window.getComputedStyle(el).visibility !== 'hidden'
  })

  if (focusableElements.length === 0) return

  const firstFocusableEl = focusableElements[0]
  const lastFocusableEl = focusableElements[focusableElements.length - 1]

  if (e.shiftKey) {
    if (document.activeElement === firstFocusableEl) {
      lastFocusableEl.focus()
      e.preventDefault()
    }
  } else {
    if (document.activeElement === lastFocusableEl) {
      firstFocusableEl.focus()
      e.preventDefault()
    }
  }
  
  setTimeout(() => {
    if (document.activeElement && !PSDropdownFloating.value.contains(document.activeElement)) {
      if (e.shiftKey) {
        lastFocusableEl.focus()
      } else {
        firstFocusableEl.focus()
      }
    }
  }, 10)
}

const open = async () => {
  show.value = true
  emit('open')
  await focusFirstElement()
}

const close = (event) => {
  if (event?.target && PSDropdownTrigger.value.contains(event?.target)) return

  show.value = false
  emit('close')
}

const toggle = async (event) => {
  if (props.disabled) return

  if (event) event.stopPropagation()

  if (!show.value) {
    await open()
  } else {
    close()
  }
}

defineExpose({ open, close, closeWithFocus, toggle })
</script>
